<script setup>
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

defineProps({
  conversations: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
});
const { t } = useI18n();
const router = useRouter();

const bucketLabel = bucket => {
  const labels = {
    low: t('PAINEL.PENDING.BUCKET_LOW'),
    medium: t('PAINEL.PENDING.BUCKET_MEDIUM'),
    high: t('PAINEL.PENDING.BUCKET_HIGH'),
    critical: t('PAINEL.PENDING.BUCKET_CRITICAL'),
  };
  return labels[bucket] || bucket;
};

const bucketClass = bucket => {
  const classes = {
    low: 'bg-n-slate-3 text-n-slate-11',
    medium: 'bg-amber-3 text-amber-9',
    high: 'bg-ruby-3 text-ruby-9',
    critical: 'bg-ruby-9 text-white',
  };
  return classes[bucket] || 'bg-n-slate-3 text-n-slate-11';
};

const formatWait = seconds => {
  if (seconds < 60) return t('PAINEL.PENDING.SECONDS', { n: seconds });
  if (seconds < 3600)
    return t('PAINEL.PENDING.MINUTES', { n: Math.floor(seconds / 60) });
  if (seconds < 86400)
    return t('PAINEL.PENDING.HOURS', { n: Math.floor(seconds / 3600) });
  return t('PAINEL.PENDING.DAYS', { n: Math.floor(seconds / 86400) });
};

const openConversation = displayId => {
  router.push({
    name: 'inbox_conversation',
    params: { conversation_id: displayId },
  });
};
</script>

<template>
  <div
    class="flex flex-col gap-3 p-4 rounded-xl outline outline-1 outline-n-container bg-n-solid-2"
  >
    <div class="flex items-center justify-between">
      <div class="text-sm font-semibold text-n-slate-12">
        {{ t('PAINEL.PENDING.TITLE') }}
      </div>
      <span
        v-if="!loading && conversations.length > 0"
        class="px-2 py-0.5 text-xs rounded-full bg-ruby-3 text-ruby-9 font-medium"
      >
        {{ conversations.length }}
      </span>
    </div>

    <div v-if="loading" class="flex flex-col gap-2">
      <div
        v-for="i in 3"
        :key="i"
        class="h-10 rounded bg-n-slate-3 animate-pulse"
      />
    </div>

    <div
      v-else-if="conversations.length === 0"
      class="py-6 text-center text-sm text-n-slate-9"
    >
      {{ t('PAINEL.PENDING.EMPTY') }}
    </div>

    <div
      v-else
      class="flex flex-col divide-y divide-n-slate-3 max-h-[11rem] overflow-y-auto pr-1"
    >
      <button
        v-for="conv in conversations"
        :key="conv.id"
        class="flex items-center justify-between py-2 text-left hover:bg-n-alpha-1 transition-colors rounded px-1"
        @click="openConversation(conv.display_id)"
      >
        <div class="flex flex-col min-w-0">
          <span class="text-sm font-medium text-n-slate-12 truncate">
            {{ conv.contact_name || t('PAINEL.PENDING.UNKNOWN_CONTACT') }}
          </span>
          <span class="text-xs text-n-slate-9">{{ conv.inbox_name }}</span>
        </div>
        <div class="flex items-center gap-2 shrink-0 ml-2">
          <span class="text-xs text-n-slate-10">{{
            formatWait(conv.waiting_seconds)
          }}</span>
          <span
            class="px-1.5 py-0.5 text-xs rounded-full font-medium"
            :class="bucketClass(conv.bucket)"
          >
            {{ bucketLabel(conv.bucket) }}
          </span>
        </div>
      </button>
    </div>
  </div>
</template>
